import usePremiumGate from '../hooks/usePremiumGate';
import React, { useState, useRef } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Slider } from './ui/slider';
import { Download, Palette, Type, Image as ImageIcon, Share2, Sparkles, Loader2 } from 'lucide-react';
import html2canvas from 'html2canvas';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Extended templates with more variety
const IMAGE_TEMPLATES = {
  gradient_purple: {
    name: 'Purple Gradient',
    style: { background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }
  },
  gradient_ocean: {
    name: 'Ocean',
    style: { background: 'linear-gradient(135deg, #2E3192 0%, #1BFFFF 100%)' }
  },
  gradient_sunset: {
    name: 'Sunset',
    style: { background: 'linear-gradient(135deg, #FA8BFF 0%, #2BD2FF 52%, #2BFF88 90%)' }
  },
  gradient_fire: {
    name: 'Fire',
    style: { background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' }
  },
  solid_dark: {
    name: 'Dark',
    style: { background: '#1e293b' }
  },
  solid_light: {
    name: 'Light',
    style: { background: '#f8fafc', color: '#1e293b' }
  },
  solid_blue: {
    name: 'Royal Blue',
    style: { background: '#1e40af' }
  },
  solid_green: {
    name: 'Emerald',
    style: { background: '#059669' }
  }
};

const NATURE_PHOTOS = [1, 2, 3, 4, 5, 6, 7].map((n) => `${BACKEND_URL}/api/tools/votd/backgrounds/${n}`);

const FONT_STYLES = {
  serif: { name: 'Serif', class: 'font-serif' },
  sans: { name: 'Sans', class: 'font-sans' },
  mono: { name: 'Mono', class: 'font-mono' }
};

const TEXT_ALIGNMENTS = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right'
};

/**
 * EnhancedVerseImageCustomizer
 * Advanced verse image creation with extensive customization
 */
const EnhancedVerseImageCustomizer = ({ isOpen, onClose, verse }) => {
  const { language } = useLanguage();
  const canvasRef = useRef(null);

  const [template, setTemplate] = useState('gradient_purple');
  const [fontStyle, setFontStyle] = useState('serif');
  const [fontSize, setFontSize] = useState(28);
  const [textAlign, setTextAlign] = useState('center');
  const [textColor, setTextColor] = useState('#ffffff');
  const [addWatermark, setAddWatermark] = useState(false);
  const [watermarkText, setWatermarkText] = useState('');
  const [generating, setGenerating] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [photo, setPhoto] = useState(null);
  const [aiPhoto, setAiPhoto] = useState(null);
  const [painting, setPainting] = useState(false);
  const { isPremium, requirePremium, dialog: premiumDialog } = usePremiumGate();

  const paintAi = async () => {
    if (!requirePremium(language === 'es' ? 'Imágenes con IA' : 'AI verse images')) return;
    setPainting(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/tools/votd/ai-background`,
        { reference: verse.reference, text: verse.text }, { withCredentials: true });
      const url = `${BACKEND_URL}${data.url}`;
      setAiPhoto(url);
      setPhoto(url);
    } catch (e) {
      const msg = e?.response?.status === 401
        ? (language === 'es' ? 'Inicia sesión para crear imágenes con IA' : 'Sign in to create AI images')
        : (e?.response?.data?.detail || (language === 'es' ? 'No se pudo crear la imagen' : 'Could not create the image'));
      toast.error(msg);
    } finally { setPainting(false); }
  };

  if (!verse) return null;

  const renderBlob = async () => {
    const canvas = await html2canvas(canvasRef.current, {
      backgroundColor: null, scale: 3, logging: false, useCORS: true, width: 1080, height: 1080,
    });
    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('render failed'))), 'image/png');
    });
  };

  const recordActivity = () => {
    axios.post(
      `${BACKEND_URL}/api/community/activity`,
      { type: 'verse_image', data: {
        verse_reference: verse.reference,
        verse_text: verse.text.substring(0, 100) + (verse.text.length > 100 ? '...' : ''),
        template: currentTemplate.name,
      }},
      { withCredentials: true },
    ).catch(() => {});
  };

  const generateImage = async () => {
    setGenerating(true);
    try {
      const blob = await renderBlob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.download = `verse-${verse.reference.replace(/\s+/g, '-')}.png`;
      link.href = url;
      link.click();
      URL.revokeObjectURL(url);
      toast.success(language === 'es' ? '¡Imagen descargada!' : 'Image downloaded!');
      recordActivity();
    } catch (error) {
      console.error('Error generating image:', error);
      toast.error(language === 'es' ? 'Error al generar imagen' : 'Failed to generate image');
    } finally {
      setGenerating(false);
    }
  };

  const shareImage = async () => {
    setSharing(true);
    try {
      const blob = await renderBlob();
      const file = new File([blob], `verse-${verse.reference.replace(/\s+/g, '-')}.png`, { type: 'image/png' });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: verse.reference,
          text: `"${verse.text}" — ${verse.reference}`,
        });
        recordActivity();
      } else {
        // No native file share (desktop) — fall back to download.
        await generateImage();
      }
    } catch (error) {
      if (error && error.name === 'AbortError') { setSharing(false); return; } // user cancelled
      console.error('Error sharing image:', error);
      toast.error(language === 'es' ? 'No se pudo compartir' : 'Could not share image');
    } finally {
      setSharing(false);
    }
  };

  const currentTemplate = IMAGE_TEMPLATES[template];
  const currentFont = FONT_STYLES[fontStyle];

  return (
    <Dialog open={isOpen} onOpenChange={(open) => {
      if (!open) onClose();
    }}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ImageIcon className="w-5 h-5" />
            {language === 'es' ? 'Crear Imagen de Versículo' : 'Create Verse Image'}
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 py-4">
          {/* Preview */}
          <div className="space-y-4">
            <Label>{language === 'es' ? 'Vista Previa' : 'Preview'}</Label>
            <div 
              ref={canvasRef}
              className={`relative aspect-square w-full max-w-[500px] mx-auto rounded-xl overflow-hidden shadow-2xl flex flex-col items-center justify-center p-12 ${currentFont.class} ${TEXT_ALIGNMENTS[textAlign]}`}
              style={{
                ...currentTemplate.style,
                ...(photo ? { backgroundImage: `url(${photo})`, backgroundSize: 'cover', backgroundPosition: 'center' } : {}),
                color: photo ? '#ffffff' : textColor
              }}
              data-testid="verse-image-preview"
            >
              {photo && <div className="absolute inset-0 bg-black/35" />}
              <div className="relative max-w-full" style={photo ? { textShadow: '0 2px 8px rgba(0,0,0,0.55)' } : undefined}>
                <p 
                  className="leading-relaxed font-medium mb-6"
                  style={{ fontSize: `${fontSize}px` }}
                >
                  "{verse.text}"
                </p>
                <p 
                  className="font-bold opacity-90"
                  style={{ fontSize: `${fontSize * 0.6}px` }}
                >
                  — {verse.reference}
                </p>
                {addWatermark && watermarkText && (
                  <p 
                    className="mt-8 text-xs opacity-50"
                  >
                    {watermarkText}
                  </p>
                )}
              </div>
              {/* Lumera stamp */}
              <div className="absolute bottom-4 left-0 right-0 flex items-center justify-center gap-1.5" data-testid="verse-image-stamp">
                <img src="/logo192.png" alt="" className="h-5 w-5 rounded" crossOrigin="anonymous" />
                <span className="text-[11px] font-bold tracking-[0.2em]" style={{ color: photo ? '#fff' : textColor, opacity: 0.85 }}>LUMERA</span>
              </div>
            </div>
          </div>

          {/* Customization Options */}
          <div className="space-y-6">
            <Tabs defaultValue="template">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="template">
                  <Palette className="w-4 h-4 mr-1" />
                  {language === 'es' ? 'Tema' : 'Theme'}
                </TabsTrigger>
                <TabsTrigger value="text">
                  <Type className="w-4 h-4 mr-1" />
                  {language === 'es' ? 'Texto' : 'Text'}
                </TabsTrigger>
                <TabsTrigger value="extra">
                  {language === 'es' ? 'Extra' : 'Extra'}
                </TabsTrigger>
              </TabsList>

              {/* Template Selection */}
              <TabsContent value="template" className="space-y-4">
                <div>
                  <Label>{language === 'es' ? 'Estilo de Fondo' : 'Background Style'}</Label>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    {Object.entries(IMAGE_TEMPLATES).map(([key, temp]) => (
                      <button
                        key={key}
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setTemplate(key);
                          setPhoto(null);
                        }}
                        className={`h-20 rounded-lg border-2 transition-all flex items-center justify-center text-xs font-semibold ${
                          template === key && !photo
                            ? 'border-purple-600 scale-105 shadow-lg' 
                            : 'border-slate-300 dark:border-slate-700 hover:border-purple-400'
                        }`}
                        style={temp.style}
                      >
                        {temp.name}
                      </button>
                    ))}
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="template" className="space-y-3">
                <Label>{language === 'es' ? 'Fotos e imágenes con IA' : 'Photos & AI images'}</Label>
                <Button type="button" variant="outline" className="w-full" onClick={paintAi} disabled={painting} data-testid="verse-image-ai-btn">
                  {painting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2 text-amber-500" />}
                  {painting
                    ? (language === 'es' ? 'Pintando una imagen para este versículo…' : 'Painting an image for this verse…')
                    : (language === 'es' ? 'Crear imagen con IA para este versículo' : 'Create an AI image for this verse')}
                  {!isPremium && <span className="ml-2 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800" data-testid="verse-image-ai-lock">Lumera+</span>}
                </Button>
                {premiumDialog}
                <div className="grid grid-cols-4 gap-2">
                  {[...(aiPhoto ? [aiPhoto] : []), ...NATURE_PHOTOS].map((url, i) => (
                    <button key={url} type="button" onClick={() => setPhoto(url)}
                      className={`aspect-square rounded-lg border-2 bg-cover bg-center transition-all ${photo === url ? 'border-purple-600 scale-105 shadow-lg' : 'border-transparent hover:border-purple-400'}`}
                      style={{ backgroundImage: `url(${url})` }}
                      data-testid={`verse-image-photo-${i}`} aria-label="Background photo" />
                  ))}
                </div>
              </TabsContent>

              {/* Text Customization */}
              <TabsContent value="text" className="space-y-4">
                <div>
                  <Label>{language === 'es' ? 'Fuente' : 'Font'}</Label>
                  <div className="grid grid-cols-3 gap-2 mt-2">
                    {Object.entries(FONT_STYLES).map(([key, font]) => (
                      <Button
                        key={key}
                        variant={fontStyle === key ? "default" : "outline"}
                        onClick={() => setFontStyle(key)}
                        className={font.class}
                      >
                        {font.name}
                      </Button>
                    ))}
                  </div>
                </div>

                <div>
                  <Label>{language === 'es' ? 'Tamaño de Texto' : 'Text Size'}: {fontSize}px</Label>
                  <Slider
                    value={[fontSize]}
                    onValueChange={([val]) => setFontSize(val)}
                    min={16}
                    max={48}
                    step={2}
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label>{language === 'es' ? 'Alineación' : 'Alignment'}</Label>
                  <div className="grid grid-cols-3 gap-2 mt-2">
                    {['left', 'center', 'right'].map((align) => (
                      <Button
                        key={align}
                        variant={textAlign === align ? "default" : "outline"}
                        onClick={() => setTextAlign(align)}
                      >
                        {align.charAt(0).toUpperCase() + align.slice(1)}
                      </Button>
                    ))}
                  </div>
                </div>

                <div>
                  <Label>{language === 'es' ? 'Color de Texto' : 'Text Color'}</Label>
                  <input
                    type="color"
                    value={textColor}
                    onChange={(e) => setTextColor(e.target.value)}
                    className="w-full h-12 rounded-lg border-2 cursor-pointer mt-2"
                  />
                </div>
              </TabsContent>

              {/* Extra Options */}
              <TabsContent value="extra" className="space-y-4">
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="watermark"
                    checked={addWatermark}
                    onChange={(e) => setAddWatermark(e.target.checked)}
                    className="rounded"
                  />
                  <Label htmlFor="watermark">
                    {language === 'es' ? 'Agregar marca de agua' : 'Add watermark'}
                  </Label>
                </div>

                {addWatermark && (
                  <div>
                    <Label>{language === 'es' ? 'Texto de marca de agua' : 'Watermark text'}</Label>
                    <input
                      type="text"
                      value={watermarkText}
                      onChange={(e) => setWatermarkText(e.target.value)}
                      placeholder={language === 'es' ? 'Ej: @miusuario' : 'e.g., @myhandle'}
                      className="w-full mt-2 px-3 py-2 border rounded-lg"
                    />
                  </div>
                )}
              </TabsContent>
            </Tabs>

            {/* Actions */}
            <div className="flex gap-2 pt-4 border-t">
              <Button onClick={generateImage} disabled={generating || sharing} className="flex-1" data-testid="verse-image-download-btn">
                <Download className="w-4 h-4 mr-2" />
                {generating
                  ? (language === 'es' ? 'Generando...' : 'Generating...')
                  : (language === 'es' ? 'Descargar' : 'Download')}
              </Button>
              <Button onClick={shareImage} disabled={generating || sharing} variant="secondary" className="flex-1" data-testid="verse-image-share-btn">
                <Share2 className="w-4 h-4 mr-2" />
                {sharing
                  ? (language === 'es' ? 'Compartiendo...' : 'Sharing...')
                  : (language === 'es' ? 'Compartir' : 'Share')}
              </Button>
              <Button onClick={onClose} variant="outline">
                {language === 'es' ? 'Cerrar' : 'Close'}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EnhancedVerseImageCustomizer;
